import { useLocation, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { PasteCard } from './paste-card'
import { ProbePanel } from './probe-panel'
import { useProbeStream } from './use-probe'
import { Page, PageHeader } from '../../components/page'
import { noMoreEvents } from '../../app/onboarding-status'
import { T } from '../../copy/text'
import { useConfirmOnboarding, useOnboarding, useParseSshInput, useStartOnboarding } from '../../app/queries'
import '../hosts/hosts.css'

/**
 * 加一台机器。**产品的命门**——这一步的成功率决定成败（PRD §4.1）。
 *
 * 三条设计要点在代码里的样子：
 *
 *   1. **输入框接受一切格式。** 一个 textarea，什么都往里贴。解析在后端（M1-09 规则版），
 *      前端不做任何格式判断，也**不做表单式追问**——缺哪一项就高亮问那一项
 *   2. **原始密码只活在这一次请求里。** 它从 textarea 直接进请求体，不进 state 之外的
 *      任何地方，不进缓存，不进 localStorage
 *   3. **全程 ≤ 40 秒，用户不用盯着。** 探测流是 SSE，一行一行长出来
 */
export function OnboardScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const search = new URLSearchParams(location.searchStr)
  const projectId = search.get('project_id') ?? undefined
  const reconnectAddress = search.get('address') ?? undefined
  const [onboardingId, setOnboardingId] = useState<string | null>(null)
  /**
   * 加密私钥的口令。
   *
   * **只在后端没解出口令、而这确实是一把私钥时才问。** 解出来了还问一遍，
   * 等于让用户做一件平台已经知道答案的事。
   */
  const [passphrase, setPassphrase] = useState('')

  const parse = useParseSshInput()
  const start = useStartOnboarding()
  const confirm = useConfirmOnboarding()
  const onboarding = useOnboarding(onboardingId)
  /**
   * 不会再有新事件了就别再连流。判据取**后端给的**（状态、完成时刻），
   * **不自己从事件里推**——事件那一侧看不出「这是最后一条」。
   *
   * **这一行原来在这里自己列了一份名单，而 `queries.ts` 里还有一份**，
   * 少的正是 `awaiting_confirm`。判据现在只有一份，见 `app/onboarding-status.ts`。
   */
  const probe = useProbeStream(onboardingId, noMoreEvents(onboarding.data))

  async function submit(raw: string): Promise<boolean> {
    const parsed = await parse.mutateAsync(raw)
    /**
     * 缺项时不提交，缺哪几项由 `PasteCard` 高亮问回去（`missing` 传给它了）。
     *
     * `secret` 为空时契约保证 `missing` 里有 `secret`，所以上面那条已经拦住了——
     * **但仍然显式再挡一道**：这一格空着而提交出去的后果是拿空密码去登录，
     * 而那会被读成「密码错了」，把人指向重置密码——**一条走不通的路**。
     *
     * 低置信那一档也走同一条路：流 A 把「address 里有空白字符」同时算进
     * `confidence` 与 `missing`，**所以一条判据管两件事，前端不用再看 confidence**。
     */
    if (!parsed.address || parsed.missing.length > 0 || !parsed.secret) return false

    try {
      const started = await start.mutateAsync({
        address: parsed.address,
        ...(parsed.port ? { port: parsed.port } : {}),
        ...(parsed.username ? { username: parsed.username } : {}),
        /**
         * **发解析出来的那把，不是整段原文。**
         *
         * 在 `SshParseResult.secret` 落地之前这里发的是 `raw`，而后端把它
         * 原样当密码用——**也就是说用密码从界面加机器从来没有成功过**：
         * 真正拿去登录的「密码」是那一整段文字。
         *
         * 而当时那不是「谁该解」的问题：解析早就发生了（`confidence` /
         * `missing` 就是它给的），**问题是解出来的密码没有字段带回来**。
         *
         * **前端一处都不自己解**——规则会变（跳板、端口、ProxyJump、
         * 各家控制台的粘贴格式），两边各解一份迟早分岔。
         *
         * 私钥那条路当时能用纯属运气：**`pem.Decode` 会跳过 BEGIN 之前的
         * 任何文字，而密码没有这种宽容。**
         */
        secret: {
          // `auth_kind` 与 `secret` 都取**后端解析出来的那个**，前端一处都不判
          kind: parsed.auth_kind === 'private_key' ? 'private_key' : 'password',
          value: parsed.secret ?? '',
          // 加密私钥的口令：优先用后端解出来的那个，用户在框里补的次之。
          //
          // 这一条是 secret 那个洞的同一个形状：sshparse 一直解得出来、
          // 登录那侧一直支持（口令不对回 FailKeyPassphrase），
          // **只是回包里没有字段带它**，于是用户得自己再打一遍。
          ...(parsed.passphrase || passphrase
            ? { passphrase: parsed.passphrase ?? passphrase }
            : {}),
        },
      })
      setOnboardingId(started.id)
    } catch {
      // start 已经把密码交出去了。失败提示走 start.error，框里不许再留 PEM/密码。
    }
    return true
  }

  const failedMessage =
    onboarding.data?.status === 'failed'
      ? onboarding.data.error?.user_message?.trim() || null
      : null

  async function accept(name?: string) {
    if (!onboardingId) return
    const suggestion = onboarding.data?.suggestion
    const chosen = name?.trim() || suggestion?.name
    const host = await confirm.mutateAsync({
      onboardingId,
      name: chosen,
      summary: suggestion?.summary,
      projectId: projectId ?? suggestion?.project_id ?? undefined,
      labelIds: suggestion?.label_ids,
    })
    await navigate({ to: '/hosts/$hostId', params: { hostId: host.id } })
  }

  return (
    <Page width="narrow" className="hosts-theme min-h-full bg-m-bg0 text-npc-ink">
      <PageHeader
        title={<T id="onboarding.page.title" />}
        subtitle={<T id="onboarding.page.subtitle" />}
      />

      <div className="mt-npc-modal grid grid-cols-2 items-start gap-npc-grid-lg">
        <PasteCard
          passphrase={passphrase}
          onPassphraseChange={
            // parse 是个 mutation，最近一次结果在 parse.data 上。
            // **只有「是私钥、且后端没解出口令」时才问**——解出来了还问，
            // 等于让用户做一件平台已经知道答案的事。
            parse.data?.auth_kind === 'private_key' && !parse.data?.passphrase
              ? setPassphrase
              : undefined
          }
          busy={parse.isPending || start.isPending}
          missing={parse.data?.missing ?? []}
          error={start.error ?? failedMessage}
          initialValue={reconnectAddress}
          onSubmit={submit}
        />

        {onboardingId ? (
          <ProbePanel
            address={onboarding.data?.address ?? ''}
            events={probe.events}
            failure={probe.failure}
            hideDuplicateMessage={failedMessage}
            suggestion={onboarding.data?.suggestion ?? null}
            ready={noMoreEvents(onboarding.data) && onboarding.data?.status !== 'failed'}
            confirming={confirm.isPending}
            onAccept={(name) => void accept(name)}
          />
        ) : null}
      </div>
    </Page>
  )
}
